import type { SignatureSummary } from "@/modules/documents";
import type { Mark } from "./signature-tones";

type Line = Extract<SignatureSummary, { signed: true }>["trust"]["chain"];
/** One line of the panel: `mark` makes it a chip, `note` is the reason under it, `mono` is for technical values. */
export type PanelRow = { label: string; value: string; mark?: Mark; note?: string; mono?: boolean };
export type PanelGroup = { id: "signer" | "integrity" | "signingTime" | "trust" | "longTerm"; rows: PanelRow[] };

const STATE_MARK: Record<Line["state"], Mark> = { confirmed: "good", "not-confirmed": "warn", unchecked: "neutral" };

/** The facts of a signed PDF grouped for the details dialog. `say` reads a text under `pdfPreview.signature`, `when` formats an ISO time. A line that was only not checked is never red. */
export function signaturePanel(s: Extract<SignatureSummary, { signed: true }>, say: (key: string) => string, when: (iso: string | null) => string): PanelGroup[] {
  const { trust } = s;
  const check = (label: string, line: Line): PanelRow => ({
    label,
    value: say(line.state === "confirmed" ? "trust.confirmed" : line.state === "unchecked" ? "trust.unchecked" : "trust.notConfirmed"),
    mark: STATE_MARK[line.state],
    note: line.reason ? say(`trust.reasons.${line.reason}`) : undefined,
  });
  const passed = (label: string, ok: boolean): PanelRow => ({ label, value: say(ok ? "ok" : "failed"), mark: ok ? "good" : "bad" });
  // a web signature is sealed with the platform's own key: the certificate names the company, not the signer, and has no qualified level to show
  const web = s.web;
  const signerRows: PanelRow[] = web
    ? [{ label: say("signer"), value: web.name || "-" }, { label: say("webEmail"), value: web.email || "-" }, { label: say("webSeal"), value: say("webSealValue") }]
    : [{ label: say("signer"), value: s.signerName || "-" }, { label: say("issuer"), value: s.issuer || "-" }, { label: say("validity"), value: `${when(s.validFrom)} - ${when(s.validTo)}` }];
  const levelRows: PanelRow[] = [
    ...(trust.service ? [{ label: say("trust.service"), value: `${trust.service.name} (${trust.service.provider}, ${trust.service.country})` }] : []),
    check(say("trust.qualified"), trust.qualifiedCertificate),
    { label: say("trust.qscd"), value: say(`trust.qscdValue.${trust.qscd}`), mark: trust.qscd === "yes" ? "good" : "neutral" },
    { label: say("trust.level"), value: trust.level ? say(`trust.levels.${trust.level}`) : say("trust.noLevel"), mark: trust.level ? "good" : "neutral" },
  ];
  return [
    { id: "signer", rows: signerRows },
    { id: "integrity", rows: [passed(say("digest"), s.digestOk), passed(say("signature"), s.signatureOk), { label: say("changed"), value: say(s.changedAfterSigning ? "yes" : "no"), mark: s.changedAfterSigning ? "bad" : "good" }] },
    { id: "signingTime", rows: [{ label: say("signedAt"), value: when(s.signedAt) }, { label: say("timestampAt"), value: when(s.timestampAt) }, check(say("trust.timestamp"), trust.timestamp), check(say("trust.timestampQualified"), trust.timestampQualified)] },
    {
      id: "trust",
      rows: [check(say("trust.chain"), trust.chain), ...(web ? [] : levelRows)],
    },
    {
      id: "longTerm",
      rows: [
        check(say("trust.validation"), trust.validationData),
        { label: say("trust.pades"), value: trust.padesLevel ?? "-", mono: true },
        { label: say("trust.basis"), value: say(`trust.basisOf.${trust.basis}`) },
        { label: say("trust.listAt"), value: trust.listAt ? when(trust.listAt) : say("trust.listNone") },
      ],
    },
  ];
}
